import type { AvailabilitiesResponse, ResourceIdTimeBlocks, TimeBlock } from '../../types'

import { differenceInMinutes } from 'date-fns'
import { filterResource } from 'util/filterResources'
import type { Resource } from '../../types/resource'
import { logFactory } from '../logFactory'

const log = logFactory('filterSlotsByResourceTypes')

interface TimeBlockWithShopId extends TimeBlock {
  shopId?: string
}

/**
 * Merges consecutive time blocks into continuous availability blocks.
 * Blocks are considered consecutive if one ends exactly when another begins.
 *
 * @example
 * Input:  [10:00-10:30, 10:30-11:00, 11:00-11:30, 13:00-13:30, 13:30-14:00]
 * Output: [10:00-11:30, 13:00-14:00]
 *
 * The first three blocks are consecutive (no gap), so they merge into one.
 * There's a gap before 13:00, so a new block starts.
 */
const mergeConsecutiveBlocks = (blocks: TimeBlockWithShopId[]): TimeBlockWithShopId[] => {
  if (blocks.length === 0) return []

  // Sort by start time
  const sorted = [...blocks].sort((a, b) => a.date_start.getTime() - b.date_start.getTime())

  const merged: TimeBlockWithShopId[] = []
  let current = { ...sorted[0] }

  for (let i = 1; i < sorted.length; i++) {
    const next = sorted[i]
    // If next block starts when current ends (or overlaps), merge them
    if (next.date_start.getTime() <= current.date_end.getTime()) {
      current.date_end = new Date(Math.max(current.date_end.getTime(), next.date_end.getTime()))
    } else {
      merged.push(current)
      current = { ...next }
    }
  }
  merged.push(current)

  return merged
}

/**
 * Calculates the intersection of two continuous availability blocks.
 *
 * @param block1 - The first time block (continuous availability)
 * @param block2 - The second time block (continuous availability)
 * @returns The intersecting time range, or null if no overlap
 */
const calculateContinuousOverlap = (block1: TimeBlockWithShopId, block2: TimeBlockWithShopId): { start: Date; end: Date } | null => {
  const start1 = block1.date_start.getTime()
  const end1 = block1.date_end.getTime()
  const start2 = block2.date_start.getTime()
  const end2 = block2.date_end.getTime()

  const overlapStart = Math.max(start1, start2)
  const overlapEnd = Math.min(end1, end2)

  if (overlapStart >= overlapEnd) {
    return null
  }

  return {
    start: new Date(overlapStart),
    end: new Date(overlapEnd),
  }
}

/**
 * Generates time slots of specified duration from a continuous time range.
 * Aligns slots to the employee's original slot boundaries when possible.
 */
const generateSlotsFromOverlap = (
  overlapStart: Date,
  overlapEnd: Date,
  slotDurationMinutes: number,
  cabinResourceId: string
): TimeBlock[] => {
  const slots: TimeBlock[] = []
  const slotDurationMs = slotDurationMinutes * 60 * 1000
  const overlapDurationMs = overlapEnd.getTime() - overlapStart.getTime()

  if (overlapDurationMs < slotDurationMs) {
    return slots // Overlap too short for even one slot
  }

  // Generate slots starting from overlap start, aligned to slot duration
  let slotStart = overlapStart.getTime()

  while (slotStart + slotDurationMs <= overlapEnd.getTime()) {
    slots.push({
      date_start: new Date(slotStart),
      date_end: new Date(slotStart + slotDurationMs),
      extraResourceIds: [cabinResourceId],
    })
    slotStart += slotDurationMs
  }

  return slots
}

/**
 * Processes employee availability blocks for shops without cabins.
 * Used for single-resource bookings (fitter only, no cabin).
 */
const processEmployeeAvailabilityWithoutCabins = (blocks: TimeBlockWithShopId[]): TimeBlock[] => {
  return blocks.map((block) => ({
    date_start: block.date_start,
    date_end: block.date_end,
  }))
}

/**
 * Processes an employee's availability against cabin availability to find overlapping slots.
 * Derives the slot duration from the employee's availability blocks.
 *
 * @param employeeId - The employee resource ID
 * @param employeeBlocks - The employee's time blocks
 * @param cabinBlocks - All cabin time blocks for the day (already filtered to integrated cabins from same shop)
 * @returns Array of time blocks where employee and cabin availability overlap
 */
const processEmployeeAvailability = (
  employeeId: string,
  employeeBlocks: TimeBlockWithShopId[],
  cabinBlocks: TimeBlockWithShopId[]
): TimeBlock[] => {
  if (employeeBlocks.length === 0) {
    log(`⚠️ No availability blocks for employee ${employeeId}`)
    return []
  }

  const slots: TimeBlock[] = []
  const employeeShopId = employeeBlocks[0].shopId

  // Derive slot duration from the employee's first availability block
  const durationInMinutes = differenceInMinutes(employeeBlocks[0].date_end, employeeBlocks[0].date_start)

  // Merge consecutive employee blocks into continuous availability
  const mergedEmployeeBlocks = mergeConsecutiveBlocks(employeeBlocks)

  const sameShopCabinBlocks = cabinBlocks.filter((cabinBlock) => cabinBlock.shopId === employeeShopId)

  // If no cabins available for this employee's shop, return empty array (no slots should be shown)
  if (sameShopCabinBlocks.length === 0) {
    log(`⚠️ No same-shop cabins for employee ${employeeId} (shop: ${employeeShopId}) - returning empty slots`)
    return []
  }

  // Group cabin blocks by cabin ID, then merge consecutive blocks within each cabin
  // This prevents incorrectly attributing time slots to the wrong cabin
  const cabinBlocksByCabinId = new Map<string, TimeBlockWithShopId[]>()
  sameShopCabinBlocks.forEach((block) => {
    const cabinId = block.extraResourceIds?.[0]
    if (!cabinId) return
    const existing = cabinBlocksByCabinId.get(cabinId) || []
    cabinBlocksByCabinId.set(cabinId, [...existing, block])
  })

  // Merge consecutive blocks within each cabin, then flatten
  const mergedCabinBlocks: TimeBlockWithShopId[] = []
  cabinBlocksByCabinId.forEach((blocks) => {
    mergedCabinBlocks.push(...mergeConsecutiveBlocks(blocks))
  })

  // Find overlaps between merged employee and cabin blocks
  mergedEmployeeBlocks.forEach((employeeContinuousBlock) => {
    mergedCabinBlocks.forEach((cabinContinuousBlock) => {
      const overlap = calculateContinuousOverlap(employeeContinuousBlock, cabinContinuousBlock)

      if (!overlap) return

      const cabinResourceId = cabinContinuousBlock.extraResourceIds?.[0]
      if (!cabinResourceId) {
        log(
          `⚠️ Skipping slot generation: missing cabinResourceId for cabin block in shop ${cabinContinuousBlock.shopId} (employee ${employeeId})`
        )
        return
      }

      const generatedSlots = generateSlotsFromOverlap(overlap.start, overlap.end, durationInMinutes, cabinResourceId)

      generatedSlots.forEach((slot) => {
        log(
          `✅ Generated slot for employee ${employeeId} + cabin ${cabinResourceId}: ${slot.date_start.toISOString()} - ${slot.date_end.toISOString()}`
        )
        slots.push(slot)
      })
    })
  })

  return slots
}

/**
 * Map of shop id -> true if that shop is CABIN_ONLY (skip fitter-cabin pairing, expose cabin-only slots).
 * Missing or false = FITTER_AND_CABIN (pair fitter with cabin).
 */
export type ShopIdToIsCabinOnly = Record<string, boolean>

/**
 * Filters availability slots to only include days where BOTH
 * employee and cabin resources have OVERLAPPING available times
 * with sufficient duration for the appointment.
 *
 * This ensures combined bookings only show slots where both
 * resource types can be reserved together for the full appointment duration.
 *
 * When shopIdToIsCabinOnly is provided: for shops with value true, employee slots
 * are skipped and that shop's cabin blocks are exposed as cabin-only slots (keyed by cabin id).
 *
 * @param availabilities - The availability data to filter
 * @param resources - Array of resources
 * @param shopIdToIsCabinOnly - Optional map: shop id -> true if CABIN_ONLY (skip pairing, add cabin-only slots)
 * @returns Filtered availability response with only valid combined slots
 */
const filterSlotsByResourceTypes = (
  availabilities: AvailabilitiesResponse | undefined,
  resources: Resource[],
  shopIdToIsCabinOnly?: ShopIdToIsCabinOnly
): AvailabilitiesResponse | undefined => {
  if (!availabilities) return undefined

  // Use Set for O(1) cabin ID lookups
  // Only include cabins that have calendar integration enabled
  const cabinIds = resources
    .filter(
      filterResource({ isCabinOnlyConfiguration: true, resourceHasCalendarIntegration: availabilities.resourceHasCalendarIntegration })
    )
    .map((resource) => resource.id)
  const cabinIdSet = new Set(cabinIds)

  log('🔍 Filtering slots by resource types:', {
    resourceCount: resources.length,
    cabinIds,
    cabinCount: cabinIdSet.size,
  })

  if (cabinIdSet.size === 0) {
    log('⚠️ No integrated cabin IDs found - returning original availabilities without filtering')
    return availabilities
  }

  const result: AvailabilitiesResponse = { ...availabilities, dayAvailability: {} }

  // Build a map of resourceId -> shop_id for quick lookup
  const resourceShopMap = new Map<string, string>(resources.map((resource) => [resource.id, resource.shop_id]))

  // Build a set of shopIds that have cabin resources with calendar integration
  const shopIdsOfCabins = new Set<string>(
    resources
      .filter(
        filterResource({ isCabinOnlyConfiguration: true, resourceHasCalendarIntegration: availabilities.resourceHasCalendarIntegration })
      )
      .map((resource) => resource.shop_id)
  )

  // Filter day availability to only include days where employee and cabin time blocks overlap
  Object.entries(availabilities.dayAvailability).forEach(([dayKey, resourceTimeBlocks]) => {
    // Group blocks by employee ID (non-cabin resources)
    const employeeBlocksById = new Map<string, TimeBlockWithShopId[]>()
    const cabinBlocks: TimeBlockWithShopId[] = []

    Object.entries(resourceTimeBlocks).forEach(([resourceId, blocks]) => {
      const shopId = resourceShopMap.get(resourceId)
      const blocksWithShopId = blocks.map((timeBlock) => ({ ...timeBlock, extraResourceIds: [resourceId], shopId }))

      if (cabinIdSet.has(resourceId)) {
        cabinBlocks.push(...blocksWithShopId)
      } else {
        const existing = employeeBlocksById.get(resourceId) || []
        employeeBlocksById.set(resourceId, [...existing, ...blocksWithShopId])
      }
    })

    log(`📅 Day ${dayKey}:`, {
      employeeCount: employeeBlocksById.size,
      cabinBlockCount: cabinBlocks.length,
      resourceIdsInAvailabilities: Object.keys(resourceTimeBlocks),
    })

    // Process each employee's availability
    const dayAvailabilityBlocks: ResourceIdTimeBlocks = {}

    employeeBlocksById.forEach((employeeBlocks, employeeId) => {
      const employeeShopId = employeeBlocks[0]?.shopId

      // CABIN_ONLY shop: skip fitter-cabin pairing; cabin-only slots are added below
      if (employeeShopId && shopIdToIsCabinOnly?.[employeeShopId] === true) {
        log(`ℹ️ Employee ${employeeId} is from CABIN_ONLY shop ${employeeShopId} - skipping fitter slots`)
        return
      }

      let slots: TimeBlock[]

      // If the employee's shop has no cabin resources, return their availability without filtering
      // This handles single-resource bookings for shops without cabins
      if (employeeShopId && !shopIdsOfCabins.has(employeeShopId)) {
        log(`ℹ️ Employee ${employeeId} is from shop ${employeeShopId} which has no cabins - returning availability without filtering`)
        slots = processEmployeeAvailabilityWithoutCabins(employeeBlocks)
      } else {
        // Shop has cabins, so filter to only show slots where both employee and cabin are available
        slots = processEmployeeAvailability(employeeId, employeeBlocks, cabinBlocks)
      }

      // Only add if there are actual slots - avoids false "partial day" detection
      if (slots.length > 0) {
        dayAvailabilityBlocks[employeeId] = slots
      }
    })

    // CABIN_ONLY shops: add cabin's raw time blocks as bookable slots keyed by cabin id.
    // Omit extraResourceIds so mergeAvailabilities produces [cabinId] as single resource_id instead of fitter+cabin pair
    if (shopIdToIsCabinOnly && Object.keys(shopIdToIsCabinOnly).length > 0) {
      cabinBlocks.forEach((block) => {
        const cabinId = block.extraResourceIds?.[0]
        const cabinShopId = block.shopId
        if (!cabinId || !cabinShopId || shopIdToIsCabinOnly[cabinShopId] !== true) return
        if (!dayAvailabilityBlocks[cabinId]) dayAvailabilityBlocks[cabinId] = []
        dayAvailabilityBlocks[cabinId].push({
          date_start: block.date_start,
          date_end: block.date_end,
        })
      })
    }

    // Only add the day if there are any slots available
    if (Object.keys(dayAvailabilityBlocks).length > 0) {
      result.dayAvailability[dayKey] = dayAvailabilityBlocks
    }
  })

  return result
}

export default filterSlotsByResourceTypes
